import { useState } from 'react';
import type { UserRecord } from '../../data/auth';
import {
  DEFAULT_PREFERENCES,
  savePreferences,
  withDefaults,
  type Preferences,
} from '../../data/preferences';
import type { DateDisplayFormat } from '../../core/dates';
import { formatDateKey, todayKey } from '../../core/dates';
import { downloadBackup } from '../../data/backup';

interface Props {
  record: UserRecord;
}

const DATE_FORMATS: DateDisplayFormat[] = ['dd-mm-yyyy', 'dd MMM yyyy', 'yyyy-mm-dd'];

/** FR-SET-01. Preferences live on the user record, not in localStorage, so they follow
 *  the user across devices — FY on desktop and calendar on the phone would be a bug. */
export function SettingsScreen({ record }: Props) {
  const [prefs, setPrefs] = useState<Preferences>(() => withDefaults(record.preferences));
  const [saved, setSaved] = useState(false);
  const today = todayKey();

  function update<K extends keyof Preferences>(key: K, value: Preferences[K]) {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    setSaved(false);
    void savePreferences(record.uid, { [key]: value }).then(() => setSaved(true));
  }

  return (
    <>
      <h1>Settings</h1>

      <section className="panel">
        <h2>Reporting</h2>

        <label className="field">
          <span>Period basis</span>
          <select
            value={prefs.periodBasis}
            onChange={(e) => update('periodBasis', e.target.value as Preferences['periodBasis'])}
          >
            <option value="fy">Financial year (Apr–Mar)</option>
            <option value="calendar">Calendar year</option>
          </select>
          <small className="muted">Governs the dashboard, transactions and reports together.</small>
        </label>

        <label className="field">
          <span>Date format</span>
          <select
            value={prefs.dateFormat}
            onChange={(e) => update('dateFormat', e.target.value as DateDisplayFormat)}
          >
            {DATE_FORMATS.map((f) => (
              <option key={f} value={f}>
                {f} — {formatDateKey(today, f)}
              </option>
            ))}
          </select>
          <small className="muted">
            The workbook displayed mm-dd-yy, which is why dates were misread.
          </small>
        </label>
      </section>

      <section className="panel">
        <h2>Entry</h2>

        <label className="field">
          <span>Default currency</span>
          <select
            value={prefs.defaultCurrency}
            onChange={(e) =>
              update('defaultCurrency', e.target.value as Preferences['defaultCurrency'])
            }
          >
            <option value="INR">INR — ₹</option>
            <option value="USD">USD — $</option>
          </select>
        </label>

        <div className="field">
          <span>Default account</span>
          <div className="placeholder small">
            Choosable once accounts exist, at M2.
          </div>
        </div>
      </section>

      <section className="panel">
        <h2>Budgets</h2>
        <label className="field">
          <span>Warn at {prefs.budgetWarnPercent}% of a budget</span>
          <input
            type="range"
            min={50}
            max={100}
            step={5}
            value={prefs.budgetWarnPercent}
            onChange={(e) => update('budgetWarnPercent', Number(e.target.value))}
          />
          <small className="muted">
            Amber at this point, red past 100%. Overspending never blocks a save.
            Budgets themselves arrive after V1.
          </small>
        </label>
      </section>

      <BackupPanel />

      <p className="muted" aria-live="polite">
        {saved ? 'Saved.' : 'Changes save as you make them.'}
        {prefs.periodBasis !== DEFAULT_PREFERENCES.periodBasis && ' Non-default basis in use.'}
      </p>
    </>
  );
}

/**
 * Manual backup (FR-BAK-03), pulled forward from M9 into M5.
 *
 * M5 is the cutover — the workbook stops being maintained. Without this there would be
 * four milestones during which the whole financial record lived in one database with no
 * way to get it out.
 */
function BackupPanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setResult(null);
    try {
      const { fileName, counts } = await downloadBackup();
      const total = Object.values(counts).filter((n) => n >= 0).reduce((a, b) => a + b, 0);
      setResult(`${fileName} — ${total} records across ${Object.keys(counts).length} collections.`);
    } catch {
      setResult('The backup did not complete. Nothing was changed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Backup</h2>
      <p className="muted small">
        A timestamped JSON of every collection, downloaded to this device. Readable
        without the app — timestamps come out as plain dates. The weekly automatic backup
        to Google Drive arrives at M9; this is here now so the cutover never happens
        without an export path.
      </p>
      <div className="row">
        <button type="button" className="btn" disabled={busy} onClick={() => void run()}>
          {busy ? 'Building…' : 'Download backup'}
        </button>
      </div>
      {result && <p className="muted small" aria-live="polite">{result}</p>}
    </section>
  );
}
